import { useState, useCallback, useMemo } from "react";
import Box from "@mui/material/Box";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Slide from "@mui/material/Slide";

import { AlertContext } from "./context";

function SlideTransition(props) {
  return <Slide {...props} direction="up" />;
}

export const AlertProvider = ({ children }) => {
  const [alerts, setAlerts] = useState([]);

  const showAlert = useCallback(
    (message, severity = "info", duration = 10000) => {
      const id = Date.now();

      setAlerts((prev) => [...prev, { id, message, severity }]);

      setTimeout(() => {
        setAlerts((prev) => prev.filter((alert) => alert.id !== id));
      }, duration);
    },
    []
  );

  const showSuccess = useCallback(
    (message, duration) => showAlert(message, "success", duration),
    [showAlert]
  );

  const showError = useCallback(
    (message, duration) => showAlert(message, "error", duration),
    [showAlert]
  );

  const showWarning = useCallback(
    (message, duration) => showAlert(message, "warning", duration),
    [showAlert]
  );

  const showInfo = useCallback(
    (message, duration) => showAlert(message, "info", duration),
    [showAlert]
  );

  const handleClose = useCallback((id) => {
    setAlerts((prev) => prev.filter((alert) => alert.id !== id));
  }, []);

  const value = useMemo(
    () => ({
      showAlert,
      showSuccess,
      showError,
      showWarning,
      showInfo,
    }),
    [showAlert, showSuccess, showError, showWarning, showInfo]
  );

  return (
    <AlertContext.Provider value={value}>
      {children}

      <Box
        sx={{
          position: "fixed",
          bottom: 16,
          left: 16,
          zIndex: 9999,
          display: "flex",
          flexDirection: "column-reverse",
          gap: 1,
          maxWidth: 400,
        }}
      >
        {alerts.map((alert) => (
          <Snackbar
            key={alert.id}
            open={true}
            TransitionComponent={SlideTransition}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            sx={{ position: "relative" }}
          >
            <Alert
              onClose={() => handleClose(alert.id)}
              severity={alert.severity}
              variant="filled"
              sx={{
                width: "100%",
                opacity: 0.8,
              }}
            >
              {alert.message}
            </Alert>
          </Snackbar>
        ))}
      </Box>
    </AlertContext.Provider>
  );
};
